import { useMemo } from 'react';

import { DataDogLogger, useDataDogLogger } from '../../../hooks/useDataDogLogger';
import { useQuery } from '../../../hooks/useQuery';
import { DATADOG_ACTIONS } from '../constants';

type DataDogActionKey = keyof typeof DATADOG_ACTIONS;

type DataDogActionsConfig<T extends DataDogActionKey> = {
  isPortalPreview: boolean | undefined;
  actionKey: T;
};

type DataDogResult<T extends DataDogActionKey> = {
  /**
   * DataDog action names for the specified action key
   * Use these with data-dd-action-name attributes for tracking
   * @example actions.decline_button_clicked
   */
  actions: (typeof DATADOG_ACTIONS)[T];

  /**
   * DataDog logger function from useDataDogLogger hook
   * Use this to log custom DataDog events programmatically
   * @example dataDogLogger('custom_event_name')
   */
  dataDogLogger: DataDogLogger;

  /**
   * Flag indicating if the app is in preview/test mode
   * true = preview mode (portal_test or isPortalPreview)
   * false = production mode
   * @example if (isPortalTest) { logger(actions.button_clicked); return; }
   */
  isPortalTest: boolean;
};

/**
 * Debug stub used in preview mode. Logs args to console instead of hitting DataDog.
 *
 * Why a plain object instead of a Proxy:
 * - Type-safety: explicit methods stay aligned with `DataDogLogger`, so
 *   adding/renaming a method on the real logger fails at compile time here too.
 *   A Proxy would silently accept any property and hide the drift.
 * - Overhead: Proxy traps bypass V8 inline caches and allocate a new closure
 *   per `get`, making method access measurably slower than a plain object.
 *   Not critical here, but no reason to pay the cost when the API is fixed.
 */
const createDebugLogger = (): DataDogLogger => {
  // Color per method so the debug stream is scannable at a glance. Matches the
  // DataDog flow-diagram palette: action=green, warning=amber, error=red,
  // context/global=blue, session=purple.
  const METHOD_COLORS: Record<string, string> = {
    log: '#2ea043',
    warning: '#d29922',
    error: '#f85149',
    setUser: '#58a6ff',
    addGlobal: '#58a6ff',
    replaceGlobal: '#58a6ff',
    addTiming: '#58a6ff',
    stopSession: '#bc8cff',
  };

  const debug = (method: string) => {
    return (...args: unknown[]) => {
      const color = METHOD_COLORS[method] ?? '#8b949e';

      // Spread args (not console.table) so objects stay expandable in devtools.
      console.log(
        `%c DD %c ${method} `,
        `background:${color};color:#fff;border-radius:3px 0 0 3px;font-weight:bold`,
        `background:#21262d;color:${color};border-radius:0 3px 3px 0;font-weight:bold`,
        ...args,
      );
    };
  };

  return {
    log: debug('log'),
    warning: debug('warning'),
    error: debug('error'),
    setUser: debug('setUser'),
    addGlobal: debug('addGlobal'),
    replaceGlobal: debug('replaceGlobal'),
    addTiming: debug('addTiming'),
    stopSession: debug('stopSession'),
  };
};

/**
 * Hook to conditionally return DataDog actions based on preview/test mode.
 *
 * @param isPortalPreview - Whether the portal is in preview mode
 * @param actionKey - The key from DATADOG_ACTIONS to return (e.g., 'vowifi', 'esim_transfer')
 * @returns Object containing DataDog actions, loggers, and preview mode flag
 *
 * @example
 * const { actions, dataDogLogger, isPortalTest } = useDataDogActions({ isPortalPreview, actionKey: 'vowifi' });
 *
 * if (isPortalTest) {
 *   dataDogLogger(actions.button_clicked);
 *   return;
 * }
 */
export const useDataDogActions = <T extends DataDogActionKey>({
  isPortalPreview,
  actionKey,
}: DataDogActionsConfig<T>): DataDogResult<T> => {
  const { getSearchParams } = useQuery();
  const dataDogLogger = useDataDogLogger();

  return useMemo(() => {
    const isPortalTest = Boolean(getSearchParams('portal_test'));
    const isPreviewMode = isPortalTest || isPortalPreview;

    // TODO: temporary — remove once DataDog is verified in dev
    const shouldTrack = !isPreviewMode;

    return {
      actions: shouldTrack
        ? DATADOG_ACTIONS[actionKey]
        : ({} as (typeof DATADOG_ACTIONS)[T]),
      dataDogLogger: shouldTrack ? dataDogLogger : createDebugLogger(),
      isPortalTest,
    };
  }, [getSearchParams, isPortalPreview, actionKey, dataDogLogger]);
};
